import { DesktopProductCard } from './DesktopProductCard';

interface Product {
  id: string;
  name: string;
  description?: string;
  category?: string;
  original_price: number;
  discounted_price: number;
  discount_rate: number;
  discount_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  stock_quantity: number;
  max_order_quantity: number | null;
  is_available: boolean;
  image_url?: string;
  ingredients?: string[];
  allergens?: string[];
  calories?: number;
  deadlineOfferAvailableToday?: boolean;
}

interface DesktopProductGridProps {
  products: Product[];
  hidingProducts: Set<string>;
  readonly: boolean;
  onProductClick: (productId: string) => void;
}

export function DesktopProductGrid({ 
  products, 
  hidingProducts, 
  readonly, 
  onProductClick 
}: DesktopProductGridProps) {
  if (products.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-xl text-gray-500">선택한 주문 유형에 해당하는 상품이 없습니다</p>
      </div>
    );
  }

  // 카테고리별로 상품을 그룹화
  const productsByCategory = products.reduce((acc, product) => {
    const category = product.category || '';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  // 랜덤박스를 제외한 카테고리들을 먼저 정렬
  const otherCategories = Object.keys(productsByCategory).filter(cat => cat !== '랜덤박스').sort();
  const hasRandomBox = '랜덤박스' in productsByCategory;

  return (
    <div className="pb-20">
      {/* 일반 카테고리들 먼저 표시 */}
      {otherCategories.map((category) => (
        <div key={category || 'uncategorized'} className="mb-12">
          <h3 className="text-2xl font-bold text-[#222222] mb-6 text-center">
            {category || '카테고리 없음'}
          </h3>
          <div className="max-w-[1400px] mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {productsByCategory[category].map((product) => (
                <DesktopProductCard
                  key={product.id}
                  product={product}
                  isHiding={hidingProducts.has(product.id)}
                  readonly={readonly}
                  onClick={() => onProductClick(product.id)}
                />
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* 랜덤박스 카테고리는 맨 아래에 표시 */}
      {hasRandomBox && (
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-[#222222] mb-6 text-center">랜덤박스</h3>
          <div className="max-w-[1400px] mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {productsByCategory['랜덤박스'].map((product) => (
                <DesktopProductCard
                  key={product.id}
                  product={product}
                  isHiding={hidingProducts.has(product.id)}
                  readonly={readonly}
                  onClick={() => onProductClick(product.id)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
